/**
 * Golden Hour: het koopvel onder de plakbalk.
 *
 * Dit bestand kiest niets zelf. Elke tik hier zoekt de echte knop in het
 * koopblok op en klikt die aan, zodat Horizon zijn eigen werk doet:
 * varianten wisselen, prijs bijwerken, voorraad nakijken. Daarna leest dit
 * bestand de pagina terug en zet het vel in dezelfde stand. Eén waarheid,
 * en die ligt in het koopblok.
 */

const SHEET = '[data-ghw-buy-sheet]';

/** De echte optiegroepen in het koopblok, op volgorde. */
function realGroups() {
  const details = document.querySelector('.product-information__grid, .product-details, product-component');
  if (!details) return [];
  return [...details.querySelectorAll('.variant-option')];
}

/** De echte radio voor één waarde binnen één optiegroep. */
function realInput(groupIndex, value) {
  const group = realGroups()[groupIndex];
  if (!group) return null;
  return (
    [...group.querySelectorAll('input[type="radio"]')].find(
      (input) => (input.value || '').trim() === value
    ) || null
  );
}

function realAddButton() {
  return document.querySelector(
    '.product-information__grid add-to-cart-component .add-to-cart-button, product-component add-to-cart-component .add-to-cart-button'
  );
}

/** Staat de maat nog open? Dezelfde vlag die het koopblok zelf gebruikt. */
function sizePending() {
  return document.documentElement.hasAttribute('data-ghw-size-pending');
}

class GhwBuySheet {
  constructor(sheet) {
    this.sheet = sheet;
    this.groups = [...sheet.querySelectorAll('[data-ghw-sheet-group]')];
    this.addButton = sheet.querySelector('[data-ghw-sheet-add]');
    this.addText = sheet.querySelector('[data-ghw-sheet-addtext]');
    this.priceEl = sheet.querySelector('[data-ghw-sheet-price]');
    this.noteEl = sheet.querySelector('[data-ghw-sheet-note]');

    this.#wireValues();
    this.#wireChrome();
    this.#wireTriggers();
    this.sync();
  }

  /* Elke knop in het vel is een afstandsbediening voor één echte radio. */
  #wireValues() {
    this.groups.forEach((group, index) => {
      group.querySelectorAll('[data-ghw-sheet-value]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = realInput(index, button.dataset.ghwSheetValue);
          if (!input) return;
          input.click();
          // Horizon werkt de picker in een eigen tik bij; daarna pas lezen.
          requestAnimationFrame(() => this.sync());
          setTimeout(() => this.sync(), 250);
        });
      });
    });
  }

  #wireChrome() {
    this.sheet.querySelector('[data-ghw-sheet-close]')?.addEventListener('click', () => this.close());

    /* Een tik náást het vel sluit het. Een dialog vangt die klik op zichzelf
       op, dus kijken we of hij buiten het eigenlijke paneel lag. */
    this.sheet.addEventListener('click', (event) => {
      if (event.target === this.sheet) this.close();
    });

    this.addButton?.addEventListener('click', () => {
      if (this.addButton.getAttribute('aria-disabled') === 'true') {
        this.#nudge();
        return;
      }
      const real = realAddButton();
      if (!real) return;
      this.close();
      real.click();
    });

    /* Kiest ze iets in het koopblok zelf, dan loopt dit vel mee. */
    document.addEventListener('change', () => this.sync(), true);
  }

  #wireTriggers() {
    /* De hele plakbalk opent het vel zolang er nog geen maat is gekozen.
       Is die er wel, dan laten we Horizons eigen knop zijn werk doen. */
    document.addEventListener(
      'click',
      (event) => {
        const bar = event.target.closest?.('.sticky-add-to-cart__bar');
        if (!bar) return;
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        if (!sizePending()) return;
        event.preventDefault();
        event.stopPropagation();
        this.open();
      },
      true
    );
  }

  #nudge() {
    const open = this.groups.find((group) =>
      !group.querySelector('[aria-pressed="true"]')
    );
    if (!open) return;
    const name = open.querySelector('.ghw-sheet__legend')?.firstChild?.textContent?.trim() || '';
    this.noteEl.textContent = name ? `Choose a ${name.toLowerCase()}` : 'Make your choice';
    open.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /** Leest het koopblok en zet het vel in dezelfde stand. */
  sync() {
    this.groups.forEach((group, index) => {
      const real = realGroups()[index];
      group.querySelectorAll('[data-ghw-sheet-value]').forEach((button) => {
        const input = realInput(index, button.dataset.ghwSheetValue);
        const isSize = real && /size/i.test(real.textContent.slice(0, 40));
        const checked = Boolean(input?.checked) && !(isSize && sizePending());
        button.setAttribute('aria-pressed', String(checked));
        const label = input?.closest('label');
        button.dataset.sold = String(Boolean(label?.classList.contains('variant-option--unavailable')));
      });

      const chosen = group.querySelector('[aria-pressed="true"]');
      const out = group.querySelector('[data-ghw-sheet-chosen]');
      if (out) out.textContent = chosen ? chosen.dataset.ghwSheetValue : '';
    });

    const ready = this.groups.every((group) => group.querySelector('[aria-pressed="true"]'));
    this.addButton?.setAttribute('aria-disabled', String(!ready));
    if (ready) this.noteEl.textContent = '';

    const price = document.querySelector('.sticky-add-to-cart__price .price__current, .sticky-add-to-cart__price');
    if (this.priceEl && price && ready) {
      this.priceEl.textContent = price.textContent.trim().split(/\s+/)[0] || '';
    } else if (this.priceEl) {
      this.priceEl.textContent = '';
    }
  }

  open() {
    if (typeof this.sheet.showModal !== 'function') return;
    if (!this.sheet.open) {
      this.sync();
      this.sheet.showModal();
    }
  }

  close() {
    if (this.sheet.open) this.sheet.close();
  }
}

const sheet = document.querySelector(SHEET);
if (sheet) new GhwBuySheet(sheet);
